import { type ComponentProps } from 'react';
import { styled } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useUpdateEffect } from 'react-use';
import { Menu as MenuIcon } from 'lucide-react';
import { isNumber } from 'lodash-es';
import { b2cVersion, b2bVersion, menuLinks } from '../utils/consts.ts';
import { useIsBlockchainVersion, useUpMedia, useUserBalance } from '../hooks';
import desktopLogo from '../assets/img/logo-with-text.svg';
import mobileLogo from '../assets/img/logo.png';
import { useMobileSidebarStore, useCurrencyStore } from '../services/store';
import { fallbackFontFamilies } from '../styles/consts.ts';
import { resetListStyles } from '../styles/helpers.ts';
import { ConnectButton } from './buttons';
import { IconButton } from './buttons/IconButton.tsx';
import { HeaderUserBalance } from './misc';

export type HeaderProps = ComponentProps<typeof Wrapper>;

const Header = (props: HeaderProps) => {
  const { t } = useTranslation();
  const blockchainVersion = useIsBlockchainVersion();

  const desktop = useUpMedia('md');

  const mobileSidebarOpened = useMobileSidebarStore(({ opened }) => opened);
  const openMobileSidebar = useMobileSidebarStore(({ open }) => open);
  const closeMobileSidebar = useMobileSidebarStore(({ close }) => close);

  // Get balance and currency to display (for both B2C and B2B)
  const { data: userBalance } = useUserBalance();
  const {
    currency: { type: currencyType },
  } = useCurrencyStore();

  useUpdateEffect(() => {
    if (desktop && mobileSidebarOpened) {
      closeMobileSidebar();
    }
  }, [desktop, mobileSidebarOpened]);


  if(!b2cVersion) {
    return null;
  }
  return (
    <Wrapper {...props}>
      <Link to="/">
        <LogoImg src={desktop ? desktopLogo : mobileLogo} alt={'Logo'} />
      </Link>
      {!b2bVersion && (
        <MobileMenuButton onClick={openMobileSidebar}>
          <MenuIcon size={24} />
        </MobileMenuButton>
      )}
      {blockchainVersion && (
        <HeaderMenu>
          <HeaderMenuList>
            {menuLinks.map(({ label, href }) => (
              <HeaderMenuListItem key={label}>
                <HeaderMenuListItemLink href={href} target={'_blank'}>
                  {t(label)}
                </HeaderMenuListItemLink>
              </HeaderMenuListItem>
            ))}
          </HeaderMenuList>
        </HeaderMenu>
      )}
      {b2cVersion && <ConnectButton />}
      {isNumber(userBalance) && (
        <BalanceWrapper>
          <HeaderUserBalance type={currencyType} balance={userBalance} />
        </BalanceWrapper>
      )}
    </Wrapper>
  );
};

const Wrapper = styled.header`
  display: flex;
  gap: 10px;
  align-items: center;
  padding: 11px 11px;
  background-color: ${({ theme }) => theme.getColor('rgbaBlockBgColor')};
  -webkit-backdrop-filter: blur(10px);
  backdrop-filter: blur(10px);
  ${({ theme }) => theme.getUpMedia('md')} {
    gap: 5%;
    padding: ${({ theme }) => theme.spacing(20, 40)};
  }
`;

const LogoImg = styled.img`
  width: 40px;

  ${({ theme }) => theme.getUpMedia('md')} {
    width: ${({ theme }) => theme.responsive(279)};
  }
`;

const MobileMenuButton = styled(IconButton).attrs({
  size: 'small',
  noBorder: true,
  bgColor: 'bgCardsActive',
  color: 'completelyWhite',
})`
  flex-shrink: 0;
  margin-right: auto;
  ${({ theme }) => theme.getUpMedia('md')} {
    display: none;
  }
`;

const HeaderMenu = styled.menu`
  display: none;
  ${({ theme }) => theme.getUpMedia('md')} {
    display: block;
    margin-right: auto;
    position: relative;
    top: ${({ theme }) => theme.responsive(3)};
  }
`;

const HeaderMenuList = styled.li`
  ${resetListStyles};
  display: flex;
  gap: ${({ theme }) => theme.responsive(20)};
`;

const HeaderMenuListItem = styled.li``;

const HeaderMenuListItemLink = styled.a`
  transition: color 0.4s ease;
  text-transform: uppercase;
  font-size: ${({ theme }) => theme.responsive(24)};
  font-weight: 600;
  font-family: Inter, ${fallbackFontFamilies};
  color: ${({ theme }) => theme.getColor('nobel')};
  &:hover {
    color: ${({ theme }) => theme.getColor('accentSecondary')};
  }
`;

const BalanceWrapper = styled.div`
  margin-left: auto;
`;

export { Header };
